<template>
<!-- STORY-002 §8: draws whatever `spans` it is given. -->
<div class="drill-timeline">
  <div class="dt-track">
    <div
      v-for="(span, i) in spans"
      :key="i"
      class="dt-area"
      :class="['dt-area--' + span.kind, { 'dt-area--pulsing': pulsing && span.kind === 'active' }]"
      :style="{ left: span.startPct + '%', width: span.widthPct + '%' }"
    ></div>
  </div>
  <div
    v-if="activeSpan"
    class="dt-marker"
    :class="{ 'dt-marker--no-transition': noTransition }"
    :style="markerStyle"
  ></div>
</div>
</template>

<script>
export default {
  name: 'DrillTimeline',
  props: {
    // [{ startPct, widthPct, kind }], kind: 'active' | 'practiced' | 'upcoming' | 'cap'.
    spans: {
      type: Array,
      default: () => []
    },
    // 0..1 inside the active span (LearningScreen listenProgress).
    progress: {
      type: Number,
      default: 0
    },
    // The marker is frozen during speak, so the active area carries the motion.
    pulsing: {
      type: Boolean,
      default: false
    },
    // STORY-002 §8c: off on seek and when not playing, or the marker glides.
    noTransition: {
      type: Boolean,
      default: false
    },
    // Scales the marker transition so it keeps pace at 0.75x/0.5x.
    playbackRate: {
      type: Number,
      default: 1
    }
  },
  computed: {
    activeSpan() {
      for (let i = 0; i < this.spans.length; i++) {
        if (this.spans[i].kind === 'active') return this.spans[i];
      }
      return null;
    },
    markerPct() {
      const a = this.activeSpan;
      if (!a) return 0;
      const p = Math.max(0, Math.min(1, this.progress));
      return a.startPct + p * a.widthPct;
    },
    markerStyle() {
      const rate = this.playbackRate || 1;
      return {
        left: this.markerPct + '%',
        transitionDuration: (1000 / rate) + 'ms'
      };
    }
  }
};
</script>

<style scoped>
.drill-timeline {
  position: relative;
  height: 12px;
  flex-shrink: 0;
  /* Active area is taller, not just coloured, so it survives at 2px wide and in greyscale. */
}

.dt-track {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 4px;
  transform: translateY(-50%);
  border-radius: 2px;
  background: var(--field); /* same track colour as .progress-track */
  overflow: visible;
}

.dt-area {
  position: absolute;
  top: 50%;
  height: 4px;
  min-width: 2px;
  transform: translateY(-50%);
  border-radius: 2px;
  background: var(--field-hover);
}

.dt-area--upcoming {
  background: var(--field-hover);
}

.dt-area--cap {
  background: var(--field);
}

.dt-area--practiced {
  background: var(--pass);
}

.dt-area--active {
  background: var(--accent);
  height: 8px; /* taller, not just a different colour - see the note above */
  z-index: 1;
}

.dt-area--active.dt-area--pulsing {
  animation: dt-pulse 1.4s ease-in-out infinite;
}

.dt-marker {
  position: absolute;
  top: 50%;
  width: 2px;
  height: 12px;
  transform: translate(-50%, -50%);
  background: var(--text);
  border-radius: 1px;
  /* STORY-002 §8c: 1Hz samples + 1s linear transition gives constant-rate
     motion without raising the poll rate (currentTime feeds other consumers). */
  transition: left 1s linear;
}

.dt-marker--no-transition {
  transition: none;
}

@keyframes dt-pulse {
  0%, 100% { opacity: 0.75; }
  50% { opacity: 1; }
}

/* The position still updates; only the animation between samples stops. */
@media (prefers-reduced-motion: reduce) {
  .dt-marker {
    transition: none;
  }
  .dt-area--active.dt-area--pulsing {
    animation: none;
  }
}
</style>
